import React from 'react'
import './user-role-editor.scss'
import { classnames } from '@halo/_utils/classnames'
import { Well, Drawer } from '@pushly/aqe/lib/components'
import { Form, Input, Switch, Radio } from 'antd'
import { IUserRole } from '@halo/interfaces/user-role'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { useForm } from 'antd/lib/form/Form'
import * as deepEqual from 'react-fast-compare'
import { UserTypeId } from '@halo/enums/user-type'

interface IUserRoleEditorProps {
    userRole: IUserRole
    onClose: () => unknown
    onSubmit: (values: Partial<IUserRole>, userRole: IUserRole) => unknown
    visible: boolean
}

const UserRoleEditor: React.FunctionComponent<IUserRoleEditorProps> = ({ userRole, ...props }) => {
    const [form] = useForm()

    const currValues = {
        name: userRole.name,
        description: userRole.description,
        user_type_id: userRole.user_type_id,
        is_active: userRole.is_active,
        is_role_override: userRole.is_role_override,
    }

    const [initialFormValues, setInitialFormValues] = React.useState(currValues)
    const [submitDisabled, setSubmitDisabled] = React.useState(true)

    /**
     * if the passed userRole values change the form should reset
     * and update initial values for submit btn state observation
     */
    React.useEffect(() => {
        if (!deepEqual(initialFormValues, currValues)) {
            setSubmitDisabled(true)
            setInitialFormValues(currValues)
            form.setFieldsValue(currValues)
        }
    }, [...Object.values(currValues)])

    return (
        <Drawer
            className={classnames('user-role-editor-drawer')}
            title={userRole.id ? `Edit: ${userRole.description ?? userRole.name}` : 'New User Role'}
            onClose={props.onClose}
            onSubmit={async () => {
                try {
                    await form.validateFields()
                    const formValues = form.getFieldsValue()

                    const changedValues: any = {}
                    for (const key of Object.keys(formValues) as Array<keyof IUserRole>) {
                        if (formValues[key] !== userRole[key]) {
                            changedValues[key] = formValues[key]
                        }
                    }

                    props.onSubmit(changedValues, userRole)
                } catch {}
            }}
            disableSubmit={submitDisabled}
            visible={props.visible ?? false}
            getContainer={false}
            destroyOnClose={true}
        >
            <Well title="User Role Details" mode="ghost" hideFooter={true}>
                <Form
                    {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                    form={form}
                    initialValues={initialFormValues}
                    onValuesChange={(_fieldKV, formKV) => setSubmitDisabled(deepEqual(formKV, initialFormValues))}
                >
                    <Form.Item name="name" label="Name" required={true}>
                        <Input disabled={!!userRole.id} />
                    </Form.Item>
                    <Form.Item name="user_type_id" label="User Type">
                        <Radio.Group size="small" buttonStyle="solid" disabled={!!userRole.id}>
                            <Radio.Button value={UserTypeId.INTERNAL}>Internal</Radio.Button>
                            <Radio.Button value={UserTypeId.EXTERNAL}>External</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item name="description" label="Description" required={true}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="is_active" label="Enabled" valuePropName="checked">
                        <Switch size="small" />
                    </Form.Item>
                    <Form.Item name="is_role_override" label="Entity Role Override" valuePropName="checked">
                        <Switch size="small" />
                    </Form.Item>
                </Form>
            </Well>
        </Drawer>
    )
}

export default UserRoleEditor
